import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Flame, Users, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const CAMP_IMG =
  "https://images.pexels.com/photos/9315792/pexels-photo-9315792.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CAMP_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/wivfg1sa_gemini_generated_video_f58c20aa.mp4";

export function FeuDeCampSection() {
  const { t, i18n } = useTranslation();
  const [regCount, setRegCount] = useState(null);
  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setRegCount(data?.campfire_registrations ?? null)).catch(() => {});
  }, []);
  const regLabel = { fr: "inscrits au feu de camp", en: "campfire sign-ups", es: "inscritos al fuego de campamento", nl: "kampvuur-inschrijvingen" }[(i18n.language || "fr").slice(0, 2)] || "inscrits au feu de camp";
  return (
    <section className="max-w-6xl mx-auto px-6 py-14 sm:py-24" data-testid="camp-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
        >
          <video
            src={CAMP_VIDEO}
            autoPlay loop muted playsInline
            preload="none"
            poster={CAMP_IMG}
            data-testid="camp-cinematic-video"
            className="w-full h-auto object-cover hero-video-fade"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
        >
          <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">{t("camp.eyebrow")}</p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-6">{t("camp.title")}</h2>
          <p className="text-foreground/65 leading-relaxed text-lg mb-8">{t("camp.body")}</p>

          {regCount != null && (
            <div className="mb-8" data-testid="camp-registrations-count">
              <span className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 border border-primary/25 text-sm text-foreground/70">
                <Flame size={14} className="text-primary/80" />
                <span className="font-display text-gradient-orange tabular-nums">{new Intl.NumberFormat(i18n.language).format(regCount)}</span>
                {regLabel}
              </span>
            </div>
          )}

          <div className="space-y-4">
            <div className="glass frame-pulse rounded-2xl p-6 flex flex-col items-center text-center gap-3" data-testid="camp-board">
              <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Flame className="text-primary" size={22} />
              </div>
              <div>
                <h3 className="font-display text-xl text-gradient-orange mb-1">{t("camp.boardTitle")}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{t("camp.boardDesc")}</p>
              </div>
            </div>
            <div className="glass frame-pulse rounded-2xl p-6 flex flex-col items-center text-center gap-3" data-testid="camp-circle">
              <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Users className="text-primary" size={22} />
              </div>
              <div>
                <h3 className="font-display text-xl text-gradient-orange mb-1">{t("camp.circleTitle")}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{t("camp.circleDesc")}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center lg:justify-start mt-8">
            <Link
              to="/entrer" data-testid="camp-cta"
              className="btn-frost-orange group inline-flex items-center gap-3 pl-7 pr-3 py-3 rounded-full font-semibold text-base"
            >
              <span>{t("camp.cta")}</span>
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[hsl(22_62%_14%_/_0.14)] transition-transform duration-500 group-hover:translate-x-1">
                <ArrowRight size={18} strokeWidth={2.2} />
              </span>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
